import { useEffect, useState } from "react";
import { X, ArrowUpRight, Check } from "lucide-react";
import { Toaster, toast } from "sonner";
import { submitLead } from "../lib/api";

const EMPTY = { name: "", email: "", company: "", project_description: "" };

export default function LeadModal({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      setDone(false);
      setForm(EMPTY);
      setErrors({});
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email))
      e.email = "Invalid email";
    if (!form.company.trim()) e.company = "Required";
    if (!form.project_description.trim()) e.project_description = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await submitLead(form);
      setDone(true);
      toast.success("Brief received.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Toaster theme="dark" position="bottom-right" />
      <div
        aria-hidden={!open}
        className={`fixed inset-0 z-[60] transition-opacity duration-500 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        data-testid="lead-modal-root"
      >
        {/* Backdrop */}
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute inset-0 bg-black/85 backdrop-blur-xl cursor-default"
        />

        {/* Panel */}
        <div
          className={`absolute inset-0 flex items-center justify-center px-6 md:px-12 py-10 transition-transform duration-700 ease-[cubic-bezier(0.2,0.7,0.2,1)] ${
            open ? "translate-y-0" : "translate-y-8"
          }`}
        >
          <div
            className="relative w-full max-w-3xl bg-[#0a0a0a] border border-white/10 p-8 md:p-14 max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onClose}
              data-testid="lead-modal-close"
              className="absolute top-5 right-5 h-10 w-10 flex items-center justify-center border border-white/10 text-white/70 hover:text-white hover:border-white/40 transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>

            {done ? (
              <div data-testid="lead-success" className="py-6 md:py-10">
                <div className="h-12 w-12 border border-white/20 flex items-center justify-center mb-8">
                  <Check size={18} />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">
                  [ Brief received ]
                </p>
                <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.95]">
                  Talk soon.
                </h2>
                <p className="mt-6 max-w-md text-neutral-300 text-lg leading-relaxed">
                  Greg will review your note personally and reply within one
                  business day.
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-10 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-white border-b border-white/40 pb-1"
                  data-testid="lead-success-close"
                >
                  Close
                  <ArrowUpRight size={14} />
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="lead-form"
                noValidate
                className="space-y-8"
              >
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">
                    [ Work With Us — Send the Brief ]
                  </p>
                  <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tighter leading-[0.95]">
                    Tell us what
                    <br />
                    you&apos;re <span className="italic font-light">making.</span>
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-2">
                  <Field
                    label="Name"
                    value={form.name}
                    onChange={update("name")}
                    error={errors.name}
                    placeholder="Your name"
                    testId="lead-input-name"
                  />
                  <Field
                    label="Email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    error={errors.email}
                    placeholder="you@studio.com"
                    testId="lead-input-email"
                  />
                </div>

                <Field
                  label="Company"
                  value={form.company}
                  onChange={update("company")}
                  error={errors.company}
                  placeholder="Company or brand"
                  testId="lead-input-company"
                />

                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">
                    Project Description
                  </label>
                  <textarea
                    value={form.project_description}
                    onChange={update("project_description")}
                    rows={4}
                    placeholder="Deliverables, timeline, audience, references..."
                    data-testid="lead-input-description"
                    className="w-full bg-transparent border-b border-white/15 py-4 text-white placeholder:text-neutral-600 focus:border-[#d26f28] focus:outline-none resize-none transition-colors"
                  />
                  {errors.project_description && (
                    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] text-red-400">
                      {errors.project_description}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-6 pt-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="lead-submit"
                    className="group inline-flex items-center gap-4 pl-8 pr-4 py-4 bg-white text-black relative overflow-hidden disabled:opacity-50"
                  >
                    <span className="relative z-10 font-mono text-[11px] uppercase tracking-[0.3em] transition-colors duration-500 group-hover:text-white">
                      {submitting ? "Sending..." : "Send Brief"}
                    </span>
                    <span className="relative z-10 flex h-9 w-9 items-center justify-center bg-[#d26f28] text-black transition-transform duration-500 group-hover:translate-x-1 group-hover:bg-white">
                      <ArrowUpRight size={16} />
                    </span>
                    <span className="absolute inset-0 bg-[#d26f28] translate-y-full transition-transform duration-500 group-hover:translate-y-0" />
                  </button>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 hidden sm:block">
                    Esc to close
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function Field({ label, value, onChange, error, type = "text", placeholder, testId }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-transparent border-b border-white/15 py-4 text-white placeholder:text-neutral-600 focus:border-[#d26f28] focus:outline-none transition-colors"
      />
      {error && (
        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}
